import type {
  AdminRole,
  AdminStatus,
  Application,
  CandidateProfile,
  EmployerDocument,
  EmployerProfile,
  Job,
  Role,
  Testimonial,
} from '@/api/types';
import { createSeedDb } from './seed';

/** A mock auth account. The access token in the app encodes `userId`. */
export interface MockUser {
  userId: string;
  /** Email/password is the primary login method. */
  email?: string;
  /** Legacy phone identifier (kept optional for admin display / older records). */
  phone?: string;
  role: Role;
  profileCompleted: boolean;
  isActive: boolean;
  /** Display name — shown on the Admin Users page. */
  name?: string;
  /** Legacy field — every admin from here on is approved by construction. */
  adminStatus?: AdminStatus;
  /** Only set for admin role — controls which write actions are available. */
  adminRole?: AdminRole;
  /** When the account was created. */
  createdAt?: string;
}

/** An email granted admin access ahead of time, not yet registered. */
export interface MockAdminInvite {
  id: string;
  email: string;
  invitedByName: string | null;
  invitedByEmail: string | null;
  createdAt: string;
}

export interface MockDb {
  users: MockUser[];
  candidateProfiles: CandidateProfile[];
  employerProfiles: EmployerProfile[];
  employerDocuments: EmployerDocument[];
  jobs: Job[];
  applications: Application[];
  /** Emulates the httpOnly refresh cookie so sessions survive page reloads. */
  sessionUserId: string | null;
  /** phone → role chosen at OTP-request time (verify carries no role). */
  pendingRole: Record<string, Role>;
  /** Emails granted admin access before they've signed up yet. */
  adminInvites: MockAdminInvite[];
  testimonials: Testimonial[];
}

const STORAGE_KEY = 'ars_mock_db_v14';

let db: MockDb | null = null;

function persistRaw(value: MockDb): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable — keep in-memory only */
  }
}

function load(): MockDb {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as MockDb;
  } catch {
    /* fall through to seed */
  }
  const seeded = createSeedDb();
  persistRaw(seeded);
  return seeded;
}

export function getDb(): MockDb {
  if (!db) db = load();
  return db;
}

/** Write the current in-memory DB back to localStorage. */
export function persist(): void {
  if (db) persistRaw(db);
}

/** Load (and seed if absent) — called once at install time. */
export function ensureSeeded(): void {
  getDb();
}

/** Wipe back to the original seed data. */
export function resetDb(): void {
  db = createSeedDb();
  persistRaw(db);
}
